// Self-serve sign-in email change, built on Supabase Auth's own
// updateUser({ email }). Supabase mails the confirmation link(s); the app only
// has to send the request and handle the callback.

export const EMAIL_CHANGE_TYPE = 'email_change';

// Where the user lands after the change: the profile page, already in edit
// mode, because that is where the Sign-in email section lives.
export const EMAIL_CHANGE_RETURN_TO = '/profile?edit=1';

export const normalizeEmailInput = (value: string) => value.trim().toLowerCase();

// Basic shape check only. Supabase rejects anything it cannot deliver to.
export const isPlausibleEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

type AuthIdentityLike = { provider?: string | null };
type AuthUserLike = {
  identities?: AuthIdentityLike[] | null;
  app_metadata?: { provider?: string; providers?: string[] } | null;
};

const providersOf = (user: AuthUserLike): string[] => {
  const fromIdentities = (user.identities ?? [])
    .map((identity) => identity.provider)
    .filter((provider): provider is string => Boolean(provider));
  if (fromIdentities.length > 0) return fromIdentities;
  return user.app_metadata?.providers ?? (user.app_metadata?.provider ? [user.app_metadata.provider] : []);
};

// A user can change their sign-in email only if they have an email identity.
// Google and Facebook accounts take their email from the provider.
export const hasEmailIdentity = (user: AuthUserLike) => providersOf(user).includes('email');

export const socialProviderLabels = (user: AuthUserLike): string[] =>
  providersOf(user)
    .filter((provider) => provider !== 'email')
    .map((provider) => provider.charAt(0).toUpperCase() + provider.slice(1));

// The address Supabase will mail. Same origin as the page that asked, because
// the PKCE code verifier lives in this browser's storage.
export const buildEmailChangeRedirect = (origin: string) =>
  `${origin}/auth/callback?type=${EMAIL_CHANGE_TYPE}`;
